import useToast from './useToast'

/**
 * Copies text to the clipboard using `document.execCommand` as a fallback method.
 * (Only for `DEV` mode)
 * @param {string} text - The text to copy.
 */
const fallbackCopy = (text: string, onSuccess?: () => void, onError?: () => void): void => {
  if (typeof window !== 'undefined') {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed' // Prevent scrolling
    document.body.appendChild(textarea)
    textarea.select()
    try {
      document.execCommand('copy')
      onSuccess?.()
    } catch (err) {
      onError?.()
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

export const useCopy = (): { copyToClipboard: (text: string, label?: string) => Promise<void> } => {
  const { notify } = useToast()

  const copyToClipboard = async (text: string, label?: string): Promise<void> => {
    const copyMessage = label ?? 'clipboard_copy'
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text)
        notify(copyMessage, { type: 'success' })
      } catch (error) {
        fallbackCopy(
          text,
          () => notify(copyMessage, { type: 'success' }),
          () => notify(copyMessage, { type: 'error' })
        )
      }
    } else {
      console.warn('Clipboard API not available, using fallback')
      fallbackCopy(
        text,
        () => notify(copyMessage, { type: 'success' }),
        () => notify(copyMessage, { type: 'error' })
      )
    }
  }

  return { copyToClipboard }
}
